import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, TrashIcon, BookOpenIcon, LockIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { AccountHierarchy } from '../../components/coa/AccountHierarchy';
import { ChartOfAccount } from '../../types/coa.types';
import { coaService } from '../../services/coa.service';
export function COADetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [account, setAccount] = useState<ChartOfAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  useEffect(() => {
    if (id) {
      loadAccount(id);
    }
  }, [id]);
  const loadAccount = async (accountId: string) => {
    try {
      const data = await coaService.getAccountById(accountId);
      setAccount(data);
    } catch (error) {
      console.error('Failed to load account:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async () => {
    if (!account) return;
    try {
      await coaService.deleteAccount(account.id);
      navigate('/coa');
    } catch (error: any) {
      alert(error.message || 'Failed to delete account');
    } finally {
      setShowDeleteModal(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center" data-id="element-974">
        <div className="text-gray-500" data-id="element-975">Loading account details...</div>
      </div>;
  }
  if (!account) {
    return <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center" data-id="element-976">
        <div className="text-gray-500" data-id="element-977">Account not found</div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-978">
      <div className="max-w-4xl mx-auto space-y-6" data-id="element-979">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-980">
          <div className="flex items-center gap-4" data-id="element-981">
            <Button variant="ghost" onClick={() => navigate('/coa')} data-id="element-982">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-983" />
            </Button>
            <div data-id="element-984">
              <div className="flex items-center gap-3" data-id="element-985">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-986">
                  {account.accountName}
                </h1>
                {account.isInactive && <Badge variant="warning" data-id="element-987">Inactive</Badge>}
                {account.hasTransactions && <Badge variant="info" data-id="element-988">Has Transactions</Badge>}
              </div>
              <p className="text-sm text-gray-600 mt-1 font-mono" data-id="element-989">
                {account.accountCode}
              </p>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-990">
            <Button variant="secondary" onClick={() => navigate(`/coa/${account.id}/ledger`)} data-id="element-991">
              <BookOpenIcon className="w-4 h-4 mr-2" data-id="element-992" />
              View Ledger
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/coa/${account.id}/edit`)} disabled={account.hasTransactions} data-id="element-993">
              {account.hasTransactions ? <>
                  <LockIcon className="w-4 h-4 mr-2" data-id="element-994" />
                  Limited Edit
                </> : <>
                  <EditIcon className="w-4 h-4 mr-2" data-id="element-995" />
                  Edit
                </>}
            </Button>
            <Button variant="danger" onClick={() => setShowDeleteModal(true)} disabled={account.hasTransactions} data-id="element-996">
              <TrashIcon className="w-4 h-4 mr-2" data-id="element-997" />
              Delete
            </Button>
          </div>
        </div>

        {/* Account Hierarchy */}
        <AccountHierarchy account={account} data-id="element-998" />

        {/* Account Details */}
        <div className="bg-white rounded-lg border p-6 space-y-6" data-id="element-999">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1000">
            Account Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1001">
            <div data-id="element-1002">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1003">
                Account Code
              </label>
              <p className="text-gray-900 font-mono" data-id="element-1004">{account.accountCode}</p>
            </div>

            <div data-id="element-1005">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1006">
                Account Name
              </label>
              <p className="text-gray-900" data-id="element-1007">{account.accountName}</p>
            </div>

            {account.accountNameSomali && <div data-id="element-1008">
                <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1009">
                  Account Name (Somali)
                </label>
                <p className="text-gray-900" data-id="element-1010">{account.accountNameSomali}</p>
              </div>}

            <div data-id="element-1011">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1012">
                Account Type
              </label>
              <p className="text-gray-900" data-id="element-1013">{account.accountType}</p>
            </div>

            <div data-id="element-1014">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1015">
                Sub Type
              </label>
              <p className="text-gray-900" data-id="element-1016">{account.subType}</p>
            </div>

            <div data-id="element-1017">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1018">
                Opening Date
              </label>
              <p className="text-gray-900" data-id="element-1019">
                {new Date(account.openingDate).toLocaleDateString()}
              </p>
            </div>

            {account.branchName && <div data-id="element-1020">
                <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1021">
                  Branch
                </label>
                <p className="text-gray-900" data-id="element-1022">{account.branchName}</p>
              </div>}

            {account.projectName && <div data-id="element-1023">
                <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1024">
                  Project
                </label>
                <p className="text-gray-900" data-id="element-1025">{account.projectName}</p>
              </div>}

            {account.prefix && <div data-id="element-1026">
                <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1027">
                  Prefix
                </label>
                <p className="text-gray-900 font-mono" data-id="element-1028">{account.prefix}</p>
              </div>}

            <div data-id="element-1029">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1030">
                Status
              </label>
              <Badge variant={account.isInactive ? 'warning' : 'success'} data-id="element-1031">
                {account.isInactive ? 'Inactive' : 'Active'}
              </Badge>
            </div>

            <div data-id="element-1032">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1033">
                Current Balance
              </label>
              <p className="text-lg font-semibold text-gray-900" data-id="element-1034">
                ${account.balance?.toLocaleString() || '0.00'}
              </p>
            </div>
          </div>

          {/* Bank Details */}
          {account.subType === 'Bank' && <div className="pt-6 border-t" data-id="element-1035">
              <h3 className="text-md font-semibold text-gray-900 mb-4" data-id="element-1036">
                Bank Account Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1037">
                <div data-id="element-1038">
                  <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1039">
                    Bank Name
                  </label>
                  <p className="text-gray-900" data-id="element-1040">{account.bankName}</p>
                </div>
                <div data-id="element-1041">
                  <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1042">
                    Bank Branch
                  </label>
                  <p className="text-gray-900" data-id="element-1043">{account.bankBranch}</p>
                </div>
                <div data-id="element-1044">
                  <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1045">
                    Account Number
                  </label>
                  <p className="text-gray-900 font-mono" data-id="element-1046">
                    {account.bankAccountNo}
                  </p>
                </div>
              </div>
            </div>}

          {/* Description */}
          {account.description && <div className="pt-6 border-t" data-id="element-1047">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1048">
                Description
              </label>
              <p className="text-gray-900" data-id="element-1049">{account.description}</p>
            </div>}

          {/* Additional Note */}
          {account.additionalNote && <div data-id="element-1050">
              <label className="block text-sm font-medium text-gray-600 mb-1" data-id="element-1051">
                Additional Note
              </label>
              <p className="text-gray-900" data-id="element-1052">{account.additionalNote}</p>
            </div>}
        </div>

        {/* Transaction Warning */}
        {account.hasTransactions && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-1053">
            <div className="flex items-start gap-3" data-id="element-1054">
              <LockIcon className="w-5 h-5 text-yellow-600 mt-0.5" data-id="element-1055" />
              <div data-id="element-1056">
                <h4 className="text-sm font-medium text-yellow-900" data-id="element-1057">
                  Account Has Transactions
                </h4>
                <p className="text-sm text-yellow-700 mt-1" data-id="element-1058">
                  This account has existing transactions and cannot be deleted.
                  Only limited edits are allowed (name, description, notes).
                </p>
              </div>
            </div>
          </div>}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Deletion" footer={<>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} data-id="element-1060">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1061">
              Delete Account
            </Button>
          </>} data-id="element-1059">
        <p className="text-gray-700" data-id="element-1062">
          Are you sure you want to delete this account? This action cannot be
          undone.
        </p>
        <div className="mt-4 p-3 bg-gray-50 rounded text-sm" data-id="element-1063">
          <div data-id="element-1064">
            <strong data-id="element-1065">Code:</strong> {account.accountCode}
          </div>
          <div data-id="element-1066">
            <strong data-id="element-1067">Name:</strong> {account.accountName}
          </div>
        </div>
      </Modal>
    </div>;
}